
import { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { useCurrencies } from "@/hooks/useCurrencies";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useTranslation } from "react-i18next";
import { Currency } from "@/lib/types";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export function DefaultCurrencySettings() {
  const { t } = useTranslation();
  const { useCurrenciesQuery } = useCurrencies();
  const { data: currencies = [] } = useCurrenciesQuery();
  const { preferences, updatePreferences } = useUserPreferences();
  const { canUseCurrencyConversion } = useFeatureAccess();
  
  const handleCurrencyChange = async (currencyId: string) => {
    await updatePreferences({
      defaultCurrencyId: currencyId
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Default Currency")}</CardTitle>
        <CardDescription>{t("Set your default currency for new contracts")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Select
          value={preferences?.defaultCurrencyId || ""}
          onValueChange={handleCurrencyChange}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={t("Select currency")} />
          </SelectTrigger>
          <SelectContent>
            {currencies.map((currency: Currency) => (
              <SelectItem key={currency.id} value={currency.id}>
                {currency.code} - {currency.name} ({currency.symbol})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {!canUseCurrencyConversion && (
          <Alert variant="default" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Your current plan only allows setting a default currency. 
              To use currency conversion features and multiple currencies, 
              upgrade your subscription plan.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
